import React, { useState } from 'react';
import {
  Card,
  CardHeader,
  Button,
  Text,
  Badge,
  makeStyles,
  tokens,
  Tooltip,
} from '@fluentui/react-components';
import {
  ChevronDownRegular,
  ChevronRightRegular,
  DocumentRegular,
  OpenRegular,
} from '@fluentui/react-icons';
import { ToolChunk } from '../models/types';

const useStyles = makeStyles({
  card: {
    marginBottom: tokens.spacingVerticalS,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    ':hover': {
      boxShadow: tokens.shadow8,
    },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flex: 1,
    minWidth: 0,
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  chunkId: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    fontFamily: tokens.fontFamilyMonospace,
  },
  title: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    marginBottom: tokens.spacingVerticalXXS,
  },
  metadataHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    paddingLeft: tokens.spacingHorizontalXL,
    paddingRight: tokens.spacingHorizontalM,
    paddingBottom: tokens.spacingVerticalS,
    paddingTop: tokens.spacingVerticalXS,
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  metadataRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    fontSize: tokens.fontSizeBase200,
    alignItems: 'baseline',
  },
  metadataKey: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
    minWidth: '120px',
    flexShrink: 0,
  },
  metadataValue: {
    color: tokens.colorNeutralForeground1,
    wordBreak: 'break-word',
    flex: 1,
  },
  content: {
    padding: tokens.spacingVerticalM,
    paddingTop: 0,
    maxHeight: '300px',
    overflowY: 'auto',
    overflowX: 'hidden',
    wordWrap: 'break-word',
  },
  contentText: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    color: tokens.colorNeutralForeground2,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  metadataSection: {
    marginTop: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusSmall,
  },
  metadataItem: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    marginBottom: tokens.spacingVerticalXXS,
    fontSize: tokens.fontSizeBase200,
  },
  metadataLabel: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
  },
  metadataValueOld: {
    color: tokens.colorNeutralForeground2,
    wordBreak: 'break-word',
  },
  linkButton: {
    minWidth: 'auto',
  },
});

interface ChunkCardProps {
  chunk: ToolChunk;
  toolName: string;
}

export const ChunkCard: React.FC<ChunkCardProps> = ({ chunk, toolName }) => {
  const classes = useStyles();
  const [isExpanded, setIsExpanded] = useState(false);

  // Get display title from metadata
  const getTitle = (): string => {
    if (toolName === 'search_bcbsne_policies' && chunk.metadata.PolicyTitleTxt) {
      return chunk.metadata.PolicyTitleTxt;
    }
    if ((toolName === 'ncd_lookup' || toolName === 'lcd_lookup') && chunk.metadata.CoverageDeterminationTitle) {
      return chunk.metadata.CoverageDeterminationTitle;
    }
    if (toolName === 'search_auth_ehr_records') {
      if (chunk.metadata.DocumentFileName) {
        return chunk.metadata.DocumentFileName;
      }
      // Fallback to Authorization + Document info
      const authId = chunk.metadata.AuthorizationId || '';
      const docGuid = chunk.metadata.DocumentGuid ? chunk.metadata.DocumentGuid.substring(0, 8) : '';
      if (authId && docGuid) {
        return `Auth ${authId} - Doc ${docGuid}`;
      }
    }
    if (toolName === 'search_appeals_ehr_records') {
      if (chunk.metadata.DocumentName) {
        return chunk.metadata.DocumentName;
      }
      // Fallback to Complaint + Document info
      const complaintId = chunk.metadata.ComplaintId || '';
      const docGuid = chunk.metadata.DocumentGuid ? chunk.metadata.DocumentGuid.substring(0, 8) : '';
      if (complaintId && docGuid) {
        return `Complaint ${complaintId} - Doc ${docGuid}`;
      }
    }
    return `Chunk ${chunk.id}`;
  };

  // Get URL from metadata if available
  const getUrl = (): string | null => {
    if (toolName === 'search_bcbsne_policies' && chunk.metadata.PolicyUrl) {
      return chunk.metadata.PolicyUrl;
    }
    if ((toolName === 'ncd_lookup' || toolName === 'lcd_lookup') && chunk.metadata.CoverageDeterminationUrl) {
      return chunk.metadata.CoverageDeterminationUrl;
    }
    return null;
  };

  // Truncate content for preview
  const getContentPreview = (): string => {
    const maxLength = 150;
    if (chunk.content.length <= maxLength) {
      return chunk.content;
    }
    return chunk.content.substring(0, maxLength) + '...';
  };

  const url = getUrl();
  const title = getTitle();

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't toggle if clicking on a button
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }
    setIsExpanded(!isExpanded);
  };

  const handleUrlClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // Get metadata entries (excluding content fields)
  const getMetadataEntries = () => {
    const excludeFields = ['PolicyContentChunk', 'CoverageDeterminationContent', 'Content', 
                           'DocumentContent', 'id'];
    
    return Object.entries(chunk.metadata).filter(
      ([key]) => !excludeFields.includes(key) && chunk.metadata[key] !== null && chunk.metadata[key] !== undefined
    );
  };

  const metadataEntries = getMetadataEntries();

  return (
    <Card className={classes.card} onClick={handleCardClick}>
      <CardHeader className={classes.header}>
        <div className={classes.headerLeft}>
          {isExpanded ? <ChevronDownRegular /> : <ChevronRightRegular />}
          <DocumentRegular />
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text className={classes.title}>{title}</Text>
          </div>
        </div>
        <div className={classes.headerRight}>
          {!isExpanded && chunk.content && (
            <Badge appearance="outline" size="small">
              {chunk.content.length} chars
            </Badge>
          )}
          {url && (
            <Tooltip content="Open source document" relationship="label">
              <Button
                appearance="subtle"
                size="small"
                icon={<OpenRegular />}
                onClick={handleUrlClick}
                className={classes.linkButton}
              />
            </Tooltip>
          )}
        </div>
      </CardHeader>

      {/* Metadata Header Section */}
      {metadataEntries.length > 0 && (
        <div className={classes.metadataHeader}>
          {metadataEntries.map(([key, value]) => (
            <div key={key} className={classes.metadataRow}>
              <span className={classes.metadataKey}>{key}:</span>
              <span className={classes.metadataValue}>{String(value)}</span>
            </div>
          ))}
        </div>
      )}
      
      {isExpanded && (
        <div className={classes.content}>
          {chunk.content && (
            <Text className={classes.contentText}>
              {chunk.content}
            </Text>
          )}
        </div>
      )}
      
      {!isExpanded && chunk.content && (
        <div style={{ 
          padding: tokens.spacingVerticalS,
          paddingLeft: tokens.spacingHorizontalM,
          paddingRight: tokens.spacingHorizontalM,
          maxHeight: '80px',
          overflow: 'hidden'
        }}>
          <Text className={classes.contentText}>
            {getContentPreview()}
          </Text>
        </div>
      )}
    </Card>
  );
};
